import { useCallback, useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { tEnglish as t, type PhraseKey } from "@buza/i18n";
import { api, apiBase, uploadFile } from "../api.js";
import { ifMatch, RefusalNotice, toRefusal, type Refusal } from "../refusal.js";

// S03 Parlour profile and branding (S-043; spec 4.1 S03, 3.3, LITE-01, NFR-05, NFR-15, NFR-16).
// Owners edit; other staff view the same details with every field read only. The server decides
// everything (it refuses a staff save and validates every field); this screen shows what
// /v1/parlour/profile returns and the reasons it gives, and keeps entries after any refusal.
// A logo is uploaded first (S-029, POST /v1/parlour/uploads/logo) and used when the form is saved.

export interface ProfileData {
  tenantId: string;
  name: string;
  slug: string;
  status: string;
  colourPrimary: string | null;
  colourSecondary: string | null;
  address: string | null;
  mainPhone: string | null;
  emergencyPhone: string | null;
  email: string | null;
  whatsappNumber: string | null;
  logo: { fileId: string; mimeType: string; width: number | null; height: number | null; bytes: number } | null;
  version: number;
  updatedAt: string;
}

type LiteItem = "logo" | "name" | "mainPhone" | "whatsappNumber" | "address";

export interface ProfileAnswer {
  profile: ProfileData;
  canEdit: boolean;
  warnings: Array<{ field: string; message: string }>;
  missingForLite: LiteItem[];
  changed?: boolean;
}

type Field = "name" | "colourPrimary" | "colourSecondary" | "address" | "mainPhone" | "emergencyPhone" | "email" | "whatsappNumber";
type Form = Record<Field, string>;

const LITE_LABEL: Record<LiteItem, PhraseKey> = {
  logo: "parlour.profile.label_logo",
  name: "parlour.profile.label_name",
  mainPhone: "parlour.profile.label_main_phone",
  whatsappNumber: "parlour.profile.label_whatsapp",
  address: "parlour.profile.label_address",
};

/** S03 and S-029: PNG or SVG up to 2 MB. Checked here only to answer at once; the server decides. */
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;
const LOGO_TYPES = ["image/png", "image/svg+xml"];

const formOf = (p: ProfileData): Form => ({
  name: p.name,
  colourPrimary: p.colourPrimary ?? "",
  colourSecondary: p.colourSecondary ?? "",
  address: p.address ?? "",
  mainPhone: p.mainPhone ?? "",
  emergencyPhone: p.emergencyPhone ?? "",
  email: p.email ?? "",
  whatsappNumber: p.whatsappNumber ?? "",
});

export const logoUrl = (fileId: string) => `${apiBase()}/v1/parlour/profile/logo/${fileId}`;

function TextField(props: {
  field: Field;
  label: PhraseKey;
  hint?: PhraseKey;
  type?: string;
  maxLength: number;
  form: Form;
  refusal: Refusal | undefined;
  warning?: string;
  /** Staff who are not owners view the fields (S03); read only keeps them focusable and readable. */
  readOnly: boolean;
  onChange: (f: Field, v: string) => void;
}) {
  const { field, form, refusal } = props;
  const reason = refusal?.fields[field];
  const described = [props.hint ? `${field}-hint` : "", reason ? `${field}-reason` : "", props.warning ? `${field}-warning` : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <div className="field">
      <label htmlFor={`s03-${field}`}>{t(props.label)}</label>
      <input
        id={`s03-${field}`}
        name={field}
        type={props.type ?? "text"}
        maxLength={props.maxLength}
        value={form[field]}
        readOnly={props.readOnly}
        onChange={(e) => props.onChange(field, e.target.value)}
        aria-invalid={!!reason}
        aria-describedby={described || undefined}
      />
      {props.hint && (
        <p className="hint" id={`${field}-hint`}>
          {t(props.hint)}
        </p>
      )}
      {reason && (
        <p className="field-error" id={`${field}-reason`}>
          {reason}
        </p>
      )}
      {props.warning && (
        <p className="warning" id={`${field}-warning`}>
          {props.warning}
        </p>
      )}
    </div>
  );
}

function ColourField(props: Omit<Parameters<typeof TextField>[0], "type" | "maxLength" | "hint">) {
  const value = props.form[props.field];
  const valid = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());
  return (
    <div className="colour-field">
      <TextField {...props} hint="parlour.profile.hint_colour" maxLength={7} />
      {valid && (
        <p className="colour-sample" style={{ color: value.trim(), background: "#ffffff" }}>
          {t("parlour.profile.colour_sample")}
        </p>
      )}
    </div>
  );
}

export function Profile() {
  const [data, setData] = useState<ProfileAnswer | undefined>();
  const [form, setForm] = useState<Form | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();
  const [refusal, setRefusal] = useState<Refusal | undefined>();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | undefined>();
  // A logo uploaded in this session and not saved yet (S-029 upload token).
  const [pendingLogo, setPendingLogo] = useState<string | undefined>();
  const [logoError, setLogoError] = useState<string | undefined>();
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const r = await api<ProfileAnswer>("/v1/parlour/profile");
    if (r.ok) {
      setData(r.data);
      setForm(formOf(r.data.profile));
      setPendingLogo(undefined);
      setRefusal(undefined);
      setLoadError(undefined);
    } else {
      setLoadError(r.status === 0 ? t("common.errors.no_connection") : (r.error?.message ?? t("parlour.profile.load_failed")));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function change(f: Field, v: string) {
    setForm((prev) => (prev ? { ...prev, [f]: v } : prev));
    setStatus(undefined);
  }

  async function chooseLogo(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    setLogoError(undefined);
    setStatus(undefined);
    if (!file) return;
    if (file.type && !LOGO_TYPES.includes(file.type)) return setLogoError(t("upload.errors.type_not_allowed"));
    if (file.size > LOGO_MAX_BYTES) return setLogoError(t("upload.errors.too_large"));
    setUploading(true);
    const r = await uploadFile<{ upload: { token: string } }>("/v1/parlour/uploads/logo", file);
    setUploading(false);
    if (r.ok) return setPendingLogo(r.data.upload.token);
    setLogoError(r.status === 0 ? t("common.errors.no_connection") : (r.error?.message ?? t("upload.errors.unreadable")));
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!data || !form) return;
    setBusy(true);
    setStatus(undefined);
    const r = await api<ProfileAnswer>("/v1/parlour/profile", {
      method: "PUT",
      body: { ...form, logoUploadToken: pendingLogo ?? null },
      headers: ifMatch(data.profile.version),
    });
    setBusy(false);
    const refused = toRefusal(r);
    setRefusal(refused);
    if (!r.ok) return;
    setData(r.data);
    setForm(formOf(r.data.profile));
    setPendingLogo(undefined);
    setStatus(r.data.changed === false ? t("parlour.profile.unchanged") : t("parlour.profile.saved"));
  }

  if (loadError && !data) {
    return (
      <section className="s03" aria-labelledby="s03-title">
        <h1 id="s03-title">{t("parlour.profile.title")}</h1>
        <p role="alert" className="error">
          {loadError}
        </p>
      </section>
    );
  }
  if (!data || !form) return <p aria-busy="true">{t("parlour.profile.loading")}</p>;

  const warningFor = (f: Field) => data.warnings.find((w) => w.field === f)?.message;
  const common = { form, refusal, readOnly: !data.canEdit, onChange: change };
  const logoShown = pendingLogo ?? data.profile.logo?.fileId;
  const logoReason = refusal?.fields.logoUploadToken;

  return (
    <section className="s03" aria-labelledby="s03-title">
      <h1 id="s03-title">{t("parlour.profile.title")}</h1>
      <p>{t("parlour.profile.intro")}</p>
      {!data.canEdit && (
        <p role="note" className="notice">
          {t("parlour.profile.view_only")}
        </p>
      )}
      {data.missingForLite.length > 0 && (
        <div className="notice lite-missing">
          <p>{t("parlour.profile.missing_title")}</p>
          <ul>
            {data.missingForLite.map((m) => (
              <li key={m}>{t(LITE_LABEL[m])}</li>
            ))}
          </ul>
        </div>
      )}
      <RefusalNotice refusal={refusal} onReload={() => void load()} />
      {status && (
        <p role="status" className="saved">
          {status}
        </p>
      )}
      <form onSubmit={save} noValidate aria-labelledby="s03-title">
        <fieldset disabled={busy}>
          <TextField field="name" label="parlour.profile.label_name" maxLength={100} {...common} />

          <div className="field logo">
            <p className="label" id="logo-label">
              {t("parlour.profile.label_logo")}
            </p>
            {logoShown ? (
              <img
                src={logoUrl(logoShown)}
                alt={pendingLogo ? t("parlour.profile.logo_new_alt") : t("parlour.profile.logo_alt")}
                className="logo-preview"
              />
            ) : (
              <p className="hint">{t("parlour.profile.no_logo")}</p>
            )}
            {pendingLogo && <p role="status">{t("parlour.profile.logo_pending")}</p>}
            {data.canEdit && (
              <>
                <label htmlFor="s03-logo" className="button">
                  {t("parlour.profile.choose_logo")}
                </label>
                <input
                  id="s03-logo"
                  type="file"
                  accept="image/png,image/svg+xml,.png,.svg"
                  onChange={(e) => void chooseLogo(e)}
                  aria-describedby="logo-hint"
                  disabled={uploading}
                />
              </>
            )}
            <p className="hint" id="logo-hint">
              {t("parlour.profile.hint_logo")}
            </p>
            {uploading && <p aria-busy="true">{t("parlour.profile.uploading")}</p>}
            {(logoError ?? logoReason) && (
              <p className="field-error" role="alert">
                {logoError ?? logoReason}
              </p>
            )}
          </div>

          <ColourField field="colourPrimary" label="parlour.profile.label_colour_primary" warning={warningFor("colourPrimary")} {...common} />
          <ColourField field="colourSecondary" label="parlour.profile.label_colour_secondary" warning={warningFor("colourSecondary")} {...common} />
          <TextField field="address" label="parlour.profile.label_address" maxLength={200} {...common} />
          <TextField field="mainPhone" label="parlour.profile.label_main_phone" hint="parlour.profile.hint_phone" type="tel" maxLength={20} {...common} />
          <TextField field="emergencyPhone" label="parlour.profile.label_emergency_phone" hint="parlour.profile.hint_optional" type="tel" maxLength={20} {...common} />
          <TextField field="email" label="parlour.profile.label_email" type="email" maxLength={254} {...common} />
          <TextField field="whatsappNumber" label="parlour.profile.label_whatsapp" hint="parlour.profile.hint_whatsapp" type="tel" maxLength={20} {...common} />

          {data.canEdit && (
            <button type="submit" disabled={busy || uploading}>
              {busy ? t("parlour.profile.saving") : t("parlour.profile.save")}
            </button>
          )}
        </fieldset>
      </form>
    </section>
  );
}
